// MCP connector SDK bridge — client-side wrappers for the mcp_connectors
// Supabase table. All writes go through the governance Supabase REST API;
// secrets (auth tokens) are written server-side via integration_secrets and
// referenced by auth_secret_id FK — they never travel through the browser
// in plain text.
//
// Runtime contract: JSON-RPC 2.0 over SSE / WebSocket / HTTP transport.
// Capability discovery (tools/list + resources/list) runs server-side on
// install and is cached in the `capabilities` jsonb column.
//
// Supabase RLS: every call requires the user's governance JWT; rows are
// filtered by jwt_active_workspace() so a user only ever sees their own
// workspace's connectors.

import { apiUrl as channelApiUrl } from '@symbo.ls/channels'
import { getClient, PROJECT_GOVERNANCE } from '../governanceBridge.js'

const _gov = () => getClient(PROJECT_GOVERNANCE)

// ── Helpers ───────────────────────────────────────────────────────────────────

const _getAccessToken = async () => {
  if (typeof window === 'undefined') return null
  const token =
    window.localStorage.getItem('symbols_bridge_access_token') ||
    window.localStorage.getItem('symbols_access_token') ||
    null
  return token
}

// Resolves via @symbo.ls/channels: SYMBOLS_API_URL > channel name
// (SYMBOLS_API_CHANNEL / SYMBOLS_APP_ENV / NODE_ENV) > defaultChannel ('next').
const _serverBase = () => {
  try { return channelApiUrl().replace(/\/+$/, '') }
  catch { return 'https://next.api.symbols.app' }
}

const _serverFetch = async (path, { method = 'GET', body } = {}) => {
  const token = await _getAccessToken()
  if (!token) return { error: 'not authenticated' }
  try {
    const res = await fetch(`${_serverBase()}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { 'Content-Type': 'application/json' } : {})
      },
      body: body ? JSON.stringify(body) : undefined
    })
    const json = await res.json().catch(() => null)
    if (!res.ok) return { error: json?.error || `HTTP ${res.status}`, status: res.status }
    return json || { error: 'empty response' }
  } catch (err) {
    return { error: err?.message || 'network error' }
  }
}

// ── Read ──────────────────────────────────────────────────────────────────────

/**
 * List all MCP connectors visible to the active workspace, optionally filtered
 * by scope.
 *
 * @param {{ scopeType?: 'org'|'workspace'|'project', scopeId?: string }} opts
 * @returns {Promise<{ data: object[], error: string|null }>}
 */
export const listConnectors = async ({ scopeType, scopeId } = {}) => {
  const client = _gov()
  if (!client) return { data: [], error: 'sdk-bridge not configured' }

  let q = client.from('mcp_connectors').select('*').order('created_at', { ascending: true })
  if (scopeType) q = q.eq('scope_type', scopeType)
  if (scopeId != null) q = q.eq('scope_id', scopeId)

  const { data, error } = await q
  return { data: data || [], error: error?.message || null }
}

// ── Write ─────────────────────────────────────────────────────────────────────

/**
 * Install a new MCP connector. Capability discovery (tools/list +
 * resources/list) is triggered server-side via POST /core/mcp-connectors;
 * this client function only creates the row and kicks the discovery task.
 *
 * @param {{ workspaceId: string, scopeType: string, scopeId?: string,
 *            name: string, url: string, transport?: string, authKind?: string,
 *            authToken?: string }} opts
 * @returns {Promise<{ ok: boolean, data?: object, error?: string }>}
 */
export const installConnector = async ({
  workspaceId,
  scopeType = 'workspace',
  scopeId = null,
  name,
  url,
  transport = 'sse',
  authKind = 'none',
  authToken
}) => {
  if (!workspaceId) return { ok: false, error: 'workspaceId required' }
  if (!url) return { ok: false, error: 'url required' }
  if (!name) return { ok: false, error: 'name required' }

  // Route through the server endpoint so it can store the secret in Vault
  // and run capability discovery without exposing the token to the DB.
  const result = await _serverFetch('/core/mcp-connectors', {
    method: 'POST',
    body: { workspaceId, scopeType, scopeId, name, url, transport, authKind, authToken }
  })

  if (result.error) return { ok: false, error: result.error }
  return { ok: true, data: result }
}

/**
 * Re-run tools/list + resources/list for a connector and refresh the
 * `capabilities` jsonb cache.
 *
 * @param {string} connectorId — UUID of the mcp_connectors row.
 * @returns {Promise<{ ok: boolean, capabilities?: object, error?: string }>}
 */
export const discoverCapabilities = async (connectorId) => {
  if (!connectorId) return { ok: false, error: 'connectorId required' }
  const result = await _serverFetch(`/core/mcp-connectors/${connectorId}/discover`, {
    method: 'POST'
  })
  if (result.error) return { ok: false, error: result.error }
  return { ok: true, capabilities: result.capabilities || result }
}

/**
 * Enable a connector (sets status = 'enabled').
 *
 * @param {string} connectorId
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const enableConnector = async (connectorId) => {
  const client = _gov()
  if (!client) return { ok: false, error: 'sdk-bridge not configured' }
  const { error } = await client
    .from('mcp_connectors')
    .update({ status: 'enabled' })
    .eq('id', connectorId)
  return error ? { ok: false, error: error.message } : { ok: true }
}

/**
 * Disable a connector (sets status = 'disabled').
 *
 * @param {string} connectorId
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const disableConnector = async (connectorId) => {
  const client = _gov()
  if (!client) return { ok: false, error: 'sdk-bridge not configured' }
  const { error } = await client
    .from('mcp_connectors')
    .update({ status: 'disabled' })
    .eq('id', connectorId)
  return error ? { ok: false, error: error.message } : { ok: true }
}

/**
 * Delete a connector row permanently.
 *
 * @param {string} connectorId
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const deleteConnector = async (connectorId) => {
  const client = _gov()
  if (!client) return { ok: false, error: 'sdk-bridge not configured' }
  const { error } = await client
    .from('mcp_connectors')
    .delete()
    .eq('id', connectorId)
  return error ? { ok: false, error: error.message } : { ok: true }
}
